import i18next from 'i18next';
import {produce} from 'immer';
import {type Moment} from 'moment-timezone';
import React from 'react';
import {useCallback, useEffect, useReducer, useState} from 'react';
import moment from 'moment-timezone';

import {checkIfFlagIsToggled, DebugLocalStorageFlags} from 'web-app/react/routes/debugger/local-storage-config';
import {type NonEmptyArray} from 'web-app/util/non-empty-array';
import {pipe} from 'web-app/util/fp';
import * as Warnable from 'web-app/util/warnable';

import {DEFAULT_STATE} from '../constants';
import {getChildrenMissingBirthday} from '../helpers/children-errors';
import {defaultPlanDateRange} from '../helpers/default-plan-date-range';
import * as Helpers from '../helpers/helpers';
import {initialBillingPeriods} from '../helpers/initialize-billing-periods';
import {secondBillingPeriodByBillingType} from '../helpers/second-billing-period';
import {reducer} from '../reducer';
import type * as Types from '../types';
import {type SimplifiedUsBillingPlan} from './use-simplified-us-billing-plan-data';

type CreateState = {
    state: Types.CreatePlanState;
    dispatch: React.Dispatch<Types.Action>;
};

type EditState = {
    state: Types.EditPlanState;
    dispatch: React.Dispatch<Types.Action>;
};

type BaseConfig = {
    simplifiedUsBillingPlan: SimplifiedUsBillingPlan;
    initiallySelectedChildIds: NonEmptyArray<string>;
    allChildren: Array<Types.Child>;
};

export type CreateConfig = BaseConfig & {
    view: 'create';
    subView: Types.CreateSubView;
    initialPlan: Types.Template | null;
};

export type EditConfig = BaseConfig & {
    view: 'edit';
    initialPlan: Types.Plan;
    initialStartDate: Types.InitialStartDate;
    biweeklyCadenceStartDate: Moment;
};

export function useSimplifiedUsBillingPlanState(config: CreateConfig): CreateState;
export function useSimplifiedUsBillingPlanState(config: EditConfig): EditState;
export function useSimplifiedUsBillingPlanState(config: CreateConfig | EditConfig): CreateState | EditState {
    const {
        simplifiedUsBillingPlan: {
            tuitions,
            billingFrequencies,
            fees,
            discounts,
            pricingGroups,
            hasAutomaticPricingGroup,
            isAutomaticBillingEnabled,
            isAutomaticDecouplingEnabled,
            isLoading,
        },
        initiallySelectedChildIds,
        allChildren,
        view,
    } = config;
    const compoundView = React.useMemo(
        () => (view === 'create' ? {view, subView: config.subView} : {view}),
        [config, view],
    );

    const selectedChildren = Helpers.selectedChildren(allChildren, initiallySelectedChildIds);
    const {monthlyBillingPeriods, weeklyBillingPeriods, biweeklyBillingPeriods} = initialBillingPeriods({
        selectedChildren,
        billingFrequencies,
        view: compoundView,
    });

    const stateInitializer = useCallback(
        (initialState: Types.State) => {
            return produce(initialState, draftState => {
                draftState.childrenState.selectedChildren = selectedChildren;
                draftState.childrenState.allChildren = allChildren;
                draftState.childrenState.allSelectedChildrenHaveBillPayer =
                    Helpers.allSelectedChildrenHaveBillPayer(selectedChildren);

                draftState.billingPeriodState.monthly = monthlyBillingPeriods;
                draftState.billingPeriodState.weekly = weeklyBillingPeriods;
                draftState.emptyPlan.enabled = checkIfFlagIsToggled(
                    DebugLocalStorageFlags.ENABLE_SAVING_EMPTY_BILLING_PLAN.flag,
                );
                draftState.isAutomaticBillingEnabled = isAutomaticBillingEnabled;
                draftState.isAutomaticDecouplingEnabled = isAutomaticDecouplingEnabled;
                draftState.view = view;
                if (draftState.view === 'create' && compoundView.view === 'create') {
                    draftState.subView = compoundView.subView;
                }
                draftState.billingFrequencyState.billingFrequencies = billingFrequencies;

                if (view === 'edit' && draftState.view === 'edit') {
                    const {planDateRange, childrenErrors} = defaultPlanDateRange({
                        initialStartDate: config.initialStartDate,
                        initialPlan: config.initialPlan,
                        biweeklyCadenceStartDate: config.biweeklyCadenceStartDate,
                        selectedChildren,
                    });
                    draftState.isPlanPartiallyBilled = config.initialStartDate.type === 'current-plan-effective-date';
                    draftState.planDateRange = planDateRange;
                    if (childrenErrors) {
                        draftState.childrenErrors = childrenErrors;
                    }

                    draftState.billingFrequencyState.initialBillingFrequency = Warnable.fallbackNullish(
                        Helpers.pickBillingFrequencyOfChildBillPayer(selectedChildren[0]),
                        'biweekly',
                        "Failed to determine billing frequency from child's bill payer.",
                    );
                }

                const childrenMissingBirthday = getChildrenMissingBirthday(
                    allChildren.filter(child => initiallySelectedChildIds.includes(child.id)),
                );
                if (childrenMissingBirthday.length > 0) {
                    draftState.childrenErrors = [
                        {
                            type: 'missing-birthday',
                            childIds: childrenMissingBirthday.map(child => child.id),
                            message: i18next.t(
                                'react.simplifiedUsBillingPlan.translations.json.childrenErrors.missingBirthday',
                            ),
                        },
                    ];
                }
                return draftState;
            });
        },
        [
            selectedChildren,
            allChildren,
            monthlyBillingPeriods,
            weeklyBillingPeriods,
            isAutomaticBillingEnabled,
            isAutomaticDecouplingEnabled,
            view,
            compoundView,
            config,
            initiallySelectedChildIds,
            billingFrequencies,
        ],
    );

    const [state, dispatch] = useReducer(reducer, DEFAULT_STATE, stateInitializer);

    const [isStateInitialised, setIsStateInitialised] = useState(false);
    useEffect(() => {
        if (!isStateInitialised && !isLoading) {
            const pricingGroupSelectPlacement = Helpers.pricingGroupSelectPlacement(
                pricingGroups,
                hasAutomaticPricingGroup,
            );
            const selectedPricingGroup = Helpers.selectedPricingGroup(pricingGroups, pricingGroupSelectPlacement);
            const {monthlyPeriodPricingGroups, weeklyPeriodPricingGroups} = pipe(
                secondBillingPeriodByBillingType({
                    monthlyBillingPeriods,
                    weeklyBillingPeriods,
                }),
                viewSpecificPeriodPricingGroups({
                    selectedPricingGroup,
                    selectedChildren,
                    config,
                }),
            );

            dispatch({
                type: 'INITIALISE_STATE',
                payload: {
                    tuitions,
                    fees,
                    discounts,
                    pricingGroupState: {
                        placement: pricingGroupSelectPlacement,
                        selectedPricingGroup,
                        hasAutomaticPricingGroup,
                        monthlyPeriodPricingGroups,
                        weeklyPeriodPricingGroups,
                    },
                    biweeklyBillingPeriods,
                },
            });

            if (config.initialPlan) {
                dispatch({
                    type: 'INITIALISE_PLAN_OR_TEMPLATE',
                    payload: {
                        plan: config.initialPlan,
                    },
                });
            }

            setIsStateInitialised(true);
        }
    }, [
        setIsStateInitialised,
        tuitions,
        isStateInitialised,
        fees,
        discounts,
        pricingGroups,
        hasAutomaticPricingGroup,
        selectedChildren,
        isLoading,
        biweeklyBillingPeriods,
        monthlyBillingPeriods,
        weeklyBillingPeriods,
        config,
    ]);

    if (view === 'edit') {
        return {
            state,
            dispatch,
        } as EditState;
    } else {
        return {
            state,
            dispatch,
        } as CreateState;
    }
}

type ViewSpecificPeriodPricingGroups = ({
    selectedPricingGroup,
    selectedChildren,
    config,
}: {
    selectedPricingGroup: null | Types.PricingGroup['id'];
    selectedChildren: Types.Child[];
    config: CreateConfig | EditConfig;
}) => ({
    secondMonthlyPeriod,
    secondWeeklyPeriod,
}: {
    secondMonthlyPeriod: Types.Month | null;
    secondWeeklyPeriod: Types.Week | null;
}) => {
    monthlyPeriodPricingGroups: null | Types.PeriodPricingGroup[];
    weeklyPeriodPricingGroups: null | Types.PeriodPricingGroup[];
};
export const viewSpecificPeriodPricingGroups: ViewSpecificPeriodPricingGroups =
    ({selectedPricingGroup, selectedChildren, config}) =>
    ({secondMonthlyPeriod, secondWeeklyPeriod}) => {
        switch (config.view) {
            case 'create':
                return {
                    monthlyPeriodPricingGroups: Helpers.initialPeriodPricingGroup(
                        secondMonthlyPeriod,
                        selectedPricingGroup,
                        selectedChildren,
                    ),
                    weeklyPeriodPricingGroups: Helpers.initialPeriodPricingGroup(
                        secondWeeklyPeriod,
                        selectedPricingGroup,
                        selectedChildren,
                    ),
                };
            case 'edit': {
                const {planDateRange} = defaultPlanDateRange({
                    initialStartDate: config.initialStartDate,
                    initialPlan: config.initialPlan,
                    biweeklyCadenceStartDate: config.biweeklyCadenceStartDate,
                    selectedChildren,
                });
                const sharedPeriodPricingGroups = Helpers.initialPeriodPricingGroup(
                    {
                        unformattedStartDate: moment(planDateRange.startDate),
                        unformattedEndDate: planDateRange.endDate ? moment(planDateRange.endDate) : null,
                    },
                    selectedPricingGroup,
                    selectedChildren,
                );
                return {
                    monthlyPeriodPricingGroups: sharedPeriodPricingGroups,
                    weeklyPeriodPricingGroups: sharedPeriodPricingGroups,
                };
            }
            default: {
                return {
                    monthlyPeriodPricingGroups: null,
                    weeklyPeriodPricingGroups: null,
                };
            }
        }
    };
